import { Component, getUnixDate, Position, sortByProperty } from "puzzle";
import { RadioButtonChangeEvent } from "@progress/kendo-react-inputs";
import * as _ from "lodash";
import { ComboBoxChangeEvent, MultiSelectChangeEvent } from "@progress/kendo-react-dropdowns";
import { FILTER_DATA, FILTER_FORM_PROPS, FILTER_FORM_STATE } from "../../Dashboard/Filters/Types";
import { PROJECT } from "../../Submission/Types";
import { commonLocalizationGetter, quickFilterChangeDelay } from "../../util/Util";

class FilterFormAbstract<GENERIC_PROPS, GENERIC_STATE> extends Component<FILTER_FORM_PROPS, FILTER_FORM_STATE> {
  defaultColor = 'no_color';
  filterChanged: boolean = false;
  state: FILTER_FORM_STATE = {
    filters: [],
    customFieldsActive: false,
    projectRadio: 'allProjects',
    projectData: [],
    publishOnLandingPageChecked: true,
    submissionCountFilterResult: 0,
    values: {
      color: this.defaultColor,
      ...this.props.value
    }
  };
  timer = 0;
  editFilterInitialData: any = {};
  paletteSettings = {
    height: 352
  };

  static defaultProps = {
    /**Displays filter name input*/
    displayFilterName: true,
    filterName: '',
    dashboardFilter: true,
    value: {}
  };

  /**
   *  Checks if filter values have been changed
   */
  updateFilterValueChanged = () => {
    if (!this.filterChanged) {
      this.filterChanged = true;
    }
  };

  /**
   *  Returns filterChanged value
   */
  getFilterChangedValue = () => this.filterChanged;

  /**
   *  Maps and sorts data before load to multi fields
   */
  getPrefilteredData = (criteria: string, dontSort?: boolean) => {
    const { filterData = [] } = this.state;
    const data: any[] = [];
    (filterData as any)[criteria] && (filterData as any)[criteria].values.map((array: any) => {
      let name = this.translate(array[0]);
      if (criteria === 'PRIORITY_LIST') {
        name = commonLocalizationGetter(`PRIORITY.${array[0]}`)
      } else if (criteria === "VENDOR_BUDGET_STATUSES") {
        name = commonLocalizationGetter(`BUDGET.statuses.${array[1]}`)
      }
      data.push({
        name,
        value: array[1]
      });
      return data
    });
    let result;
    if (criteria === 'PRIORITY_LIST') {
      result = _.orderBy(data, ['value'], ['desc']);
    } else {
      result = dontSort ? data : sortByProperty(data, 'name');
    }

    return result
  };

  /**
   * Get property from filter data
   * @param criteria
   * @param propertyName
   */
  getPropertyFromFilterData = (criteria: string, propertyName: string) => {
    const { filterData } = this.state;
    const property = (filterData as any)?.[criteria]?.[propertyName];

    return property;
  }

  /**
   * Handle if there is need to pass event to parent, otherwise calls getSubmissionCount
   *
   * @param name
   * @param value
   * @param customFieldChange
   */
  handleParentChange = (name: string, value: any, customFieldChange?: boolean) => {
    this.updateFilterValueChanged();
    const outerCb: any = this.props.onChange;
    if (outerCb) {
      if (customFieldChange) {
        outerCb && outerCb(name, value, customFieldChange);
      } else {
        outerCb && outerCb(name, value);
      }
    } else {
      let submissionCountData = { ...this.state.values };
      const phasesName = this.getPropertyFromFilterData('PHASES_REMOTE_COMBO', 'name');
      submissionCountData = this.parseRemoteComboData(phasesName, submissionCountData);
      this.dispatch('getSubmissionCount', Position.end, {
        mainGridFilter: _.omit(submissionCountData, 'filterId'),
        createFilterForm: true,
        fireMainGridFilter: false
      })
    }
  };

  /**
   * Handle radio button change
   */
  handleRadioChange = (event: RadioButtonChangeEvent) => {
    const value = event.value;
    this.setState({
      projectRadio: value
    });

    const { values } = this.state;
    if (value === 'allProjects' && values.projectValues?.length) {
      this.dispatch('getSearchBarData');
    }

    this.onFilterChange('projectValues', null); //clear selected project filter
  };

  /**
   * Load filter data
   *
   * @param filterData
   * @param projectData
   */
  loadFilterData = (filterData: FILTER_DATA, projectData: PROJECT[]) => {
    this.setState({ filterData, projectData })
  };

  /**
   * Store changes to state on data (combo and tag fields) change
   *
   * @param event
   */
  onChangeMulti = (event: ComboBoxChangeEvent | MultiSelectChangeEvent) => {
    let value = event.target.value;
    if (_.isArray(value)) {
      value = [...value];
    }
    const name = event.target.name || event.target.props.name || '';
    this.setState((oldState: any) => ({
      ...oldState,
      values: {
        ...oldState.values,
        [name]: value
      }
    }), () => this.handleDebouncedParentChange(name, value));
  };

  /**
   * Calls handleParentChange with 500ms delay
   */
  handleDebouncedParentChange = _.debounce((name: string, value: any) => {
    this.handleParentChange(name, value)
  }, quickFilterChangeDelay);
  /**
   * Store changes to state on date change
   *
   * @param event
   */
  onDateChange = (event: any): void => {
    const value = event.target.value;
    const name = event.target.name || '';
    this.setState((oldState: any) => {
      return {
        ...oldState,
        values: {
          ...oldState.values,
          [name]: value ? getUnixDate(value) : value
        }
      }
    }, () => this.handleParentChange(name, value));
  };

  /**
   * Prepare filter data and set to state
   */
  onFilterChange = (name: string, value: string | number | null | boolean) => {
    this.setState((oldState: any) => {
      return {
        ...oldState,
        values: {
          ...oldState.values,
          [name]: value
        }
      }
    }, () => {
      clearTimeout(this.timer);
      (this.timer as any) = setTimeout(() => {
        this.handleParentChange(name, value);
      }, 300);
    });
  };

  /**
   * Handle input change and set value to state. Fires onChange method on parent only if user has delay
   * on typing more then a second
   *
   * @param event
   */
  onInputChange = (event: any) => {
    const value = event.target.value;
    const name = event.target.name || '';
    this.onFilterChange(name, value);
  };

  /**
   * Load filter data on project change
   */
  onProjectChange = (event: ComboBoxChangeEvent | MultiSelectChangeEvent) => {
    const value = event.value;
    if (value.length) {
      const projectIds = value.map((item: any) => item.value);
      const params = {
        projectIds
      };
      this.dispatch('clearFormAndGetProjectRelatedData', '', { params });
    } else {
      this.dispatch('getSearchBarData');
    }

    this.onChangeMulti(event);
  };

  /**
   * Parse data from remote combo
   * @param criteria
   * @param data
   */
  parseRemoteComboData = (criteria: string, data: any) => {
    let remoteComboData = (data as any)[criteria];
    if (remoteComboData && remoteComboData.length) {
      remoteComboData = remoteComboData.map((item: any) => item.value);
    }

    const returnValue = { ...data };
    Object.assign(returnValue,
      remoteComboData ? { [criteria]: remoteComboData } : null
    );

    return returnValue;
  };

  /**
   * Validate all fields connected to form control and return global validity
   */
  validateForm = (): boolean => this.props.form.runFullValidation();

}

export default FilterFormAbstract;
